import { fetchDMContent, fetchMedia } from "./Api";
import { DM_API_KEY, DM_DMS_IMAGE_URL, DM_OBJECT_URL, DM_SEARCH_URL } from "../Settings";

/*
 * DigitaltMuseum (DM) search, used by the DM view (presentationType "dm").
 * Searches go to DM's Solr API. The search from the CMS (`dmcontent`) is the base of everything shown;
 * filters chosen on screen only narrow it down.
 */

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/** Number of objects fetched per page */
const PAGE_SIZE = 40;

/** Solr field holding the production year */
const YEAR_FIELD = "artifact.ingress.production.fromYear";

const MUSEUM_API_URL = "https://apigateway.dimu.org/museum-api/museums";

/** Time periods in the period filter. `unknown` matches objects without a production year */
export const DM_PERIODS = [
  { key: "pre1800", query: `${YEAR_FIELD}:[* TO 1799]` },
  { key: "1800-1899", query: `${YEAR_FIELD}:[1800 TO 1899]` },
  { key: "1900-1949", query: `${YEAR_FIELD}:[1900 TO 1949]` },
  { key: "1950-1999", query: `${YEAR_FIELD}:[1950 TO 1999]` },
  { key: "2000-", query: `${YEAR_FIELD}:[2000 TO *]` },
  { key: "unknown", query: `(*:* -${YEAR_FIELD}:[* TO *])` }
];

/**
 * Sort keys accepted in `settings.sort`, and the Solr sort they map to. Objects without a production year
 * are sorted last when sorting by year. "random" uses a seed, so the order is stable while paging.
 */
const SORTS: Record<string, string | ((seed: number) => string)> = {
  relevance: "score desc",
  publishedDate: "artifact.publishedDate desc",
  updatedDate: "artifact.updatedDate desc",
  title: "artifact.ingress.title asc",
  yearDesc: `def(${YEAR_FIELD},-9999) desc`,
  yearAsc: `def(${YEAR_FIELD},9999) asc`,
  random: (seed: number) => `random_${seed} asc`,
  // Hand-picked selections: the order from the CMS, applied after fetching (see useDMSearch)
  manual: "score desc"
};

/** Max number of objects in a hand-picked selection (fetched in one request to keep the CMS order) */
export const DM_MANUAL_MAX = 200;

/** Sort used when settings.sort is missing. Relevance is not used: without a query all objects score the same */
export const DM_DEFAULT_SORT_KEY = "publishedDate";

/** Sort options in the sort dropdown. The default sort from the CMS is added first if it is not in this list */
export const DM_USER_SORT_KEYS = ["publishedDate", "title"];

/** Filters in the toolbar, in display order. Each can be turned off with settings.toolbar.<key> = false */
export const DM_FILTER_KEYS = ["objectType", "museum", "period", "subject"] as const;

const SCHOOL_SUBTYPES = "artifact.subtype:(school_digital OR school_physical)";

/**
 * DM shows school programmes and virtual experiences as separate object types, although they are articles
 * with a subtype. These pseudo types are used in the type filter and in `dmcontent.search.objectType`, like on DM:
 * - VirtualExperience is only `vm-exhibition`. Other `vm-*` subtypes are parts of a virtual experience
 * - Article excludes school, all `vm-*` and museum pages (`museum`)
 */
const TYPE_QUERIES: Record<string, string> = {
  School: SCHOOL_SUBTYPES,
  VirtualExperience: "artifact.subtype:vm-exhibition",
  Article: `(artifact.type:Article -${SCHOOL_SUBTYPES} -artifact.subtype:vm-* -artifact.subtype:museum)`
};

/** Older names for the pseudo types, still accepted from the CMS */
const TYPE_ALIASES: Record<string, string> = { VM: "VirtualExperience" };

/** Object types that open as a new list of their objects, and the Solr field that links objects to them */
const CONTEXT_FIELDS: Record<string, string> = {
  Exhibition: "artifact.exhibitionUids",
  Folder: "artifact.folderUids"
};

// ---------------------------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------------------------

/** A DM object as used in the views, from a Solr search result */
export interface DMItem {
  uniqueId: string,
  uuid?: string,
  title?: string,
  /** DM type (artifact.type), e.g. "Thing" or "Article" */
  type?: string,
  subtype?: string,
  /** Owner code (identifier.owner), e.g. "ROM" or "S-SK" */
  ownerCode?: string,
  /** Default image (DMS identifier). Only set if the object has images */
  mediaIdentifier?: string,
  /** Production year(s), e.g. "1965" or "1960–1970" */
  year?: string
}

/** A DM object with the owner's name, when it is known up front (e.g. objects in articles) */
export type DMItemWithOwner = DMItem & { ownerName?: string };

/** Exhibition or folder that restricts a search, or a single object shown on its own */
export interface DMContext extends DMItem {
  /** Filter for the objects in an exhibition or folder, or in a hand-picked selection. Undefined for single objects */
  fq?: string,
  /** Unique IDs of a hand-picked selection, in the order chosen in the CMS */
  order?: string[]
}

/** Base search defined in the CMS. Filters chosen on screen are added on top of it */
export interface DMBaseSearch {
  q: string,
  fq: string[],
  /** Default sort key from the CMS (a SORTS key, or "cms" for a DM field name) */
  sortKey: string,
  /** Solr sort for the "cms" sort key */
  sort?: string,
  /** Hand-picked selection: unique IDs in the order chosen in the CMS, used by the "manual" sort */
  order?: string[]
}

/** Filters chosen on screen */
export interface DMUserFilters {
  type?: string,
  /** Owner (museum) code */
  owner?: string,
  period?: string,
  subject?: string
}

export interface DMFacet {
  value: string,
  count: number
}

export interface DMFacets {
  types: DMFacet[],
  owners: DMFacet[],
  subjects: DMFacet[],
  /** `value` is a DM_PERIODS key */
  periods: DMFacet[]
}

/** One experience (3D room, 360, story, quiz, ...) in a virtual experience */
export interface DMExperience {
  contentId: string,
  type?: string,
  name?: string,
  ingress?: string,
  previewDmsId?: string
}

// ---------------------------------------------------------------------------------------------------------------
// Object helpers
// ---------------------------------------------------------------------------------------------------------------

/**
 * Format a year span
 * @returns {string} "1965", "1960–1970" or ""
 */
export const formatDMYears = (fromYear?: number | null, toYear?: number | null): string => {
  return fromYear && toYear && fromYear !== toYear ? `${fromYear}–${toYear}` : String(fromYear || toYear || "");
}

/**
 * Convert a Solr result document to a DMItem
 * @param {object} doc Solr document
 * @returns {DMItem} Item
 */
export const toDMItem = (doc: any): DMItem => ({
  uniqueId: doc?.["artifact.uniqueId"],
  uuid: doc?.["artifact.uuid"],
  title: doc?.["artifact.ingress.title"],
  type: doc?.["artifact.type"],
  subtype: doc?.["artifact.subtype"],
  ownerCode: doc?.["identifier.owner"],
  mediaIdentifier: doc?.["artifact.hasPictures"] ? doc?.["artifact.defaultMediaIdentifier"] : undefined,
  year: formatDMYears(doc?.["artifact.ingress.production.fromYear"], doc?.["artifact.ingress.production.toYear"])
});

/**
 * Get the type key used for labels (dm.type.* / dm.types.*), taking the pseudo types into account
 * @returns {string} Type key, e.g. "Thing", "School" or "VirtualExperience"
 */
export const getDMTypeKey = (type?: string, subtype?: string): string => {
  if (subtype?.startsWith("school_")) { return "School"; }
  if (subtype?.startsWith("vm-")) { return "VirtualExperience"; }
  return type || "";
}

/** Remove the size (?dimension=…) from a DM image URL, so the full image is used */
export const cleanDMImageUrl = (url?: string): string => (url || "").replace(/[?]dimension=[^&]+&?/, "");

/** Read includeLink from the CMS, which sends a boolean or the string "true" */
export const isDMIncludeLink = (value: any): boolean => value === true || value === "true";

/** Check if an object is a virtual experience (opened in an iframe) */
export const isDMVirtualExperience = (subtype?: string): boolean => subtype === "vm-exhibition";

/** Check if an object is a part of a virtual experience (e.g. `vm-scrollytell`), shown inside the experience it belongs to */
export const isDMVirtualExperiencePart = (subtype?: string): boolean => Boolean(subtype?.startsWith("vm-")) && !isDMVirtualExperience(subtype);

/** Check if an object is a virtual experience or a part of one (all `vm-` subtypes) */
export const isDMVirtualExperienceOrPart = (subtype?: string): boolean => Boolean(subtype?.startsWith("vm-"));

/** Check if an object is an article (shown as an article page) */
export const isDMArticle = (subtype?: string): boolean => subtype === "article";

/** Check if an object is an exhibition or folder (opened as a new list of its objects) */
export const isDMContextType = (type?: string): boolean => Boolean(type && CONTEXT_FIELDS[type]);

/** Check if an object opens as a page of its own instead of in the object modal */
export const opensAsDMPage = (item: DMItem): boolean => isDMContextType(item.type) || isDMVirtualExperienceOrPart(item.subtype) || isDMArticle(item.subtype);

/** Build a DMS image URL (without dimension) */
export const getDMImageUrl = (mediaIdentifier: string): string => DM_DMS_IMAGE_URL.replace("{id}", mediaIdentifier);

/**
 * Convert a Sketchfab model URL (e.g. from DM `references`) to an embed URL, with the same player options
 * as the Sketchfab lightbox
 * @returns {string|undefined} Embed URL, or undefined if the URL is not a Sketchfab model
 */
export const getSketchfabEmbedUrl = (url?: string): string | undefined => {
  const id = typeof url === "string" && /sketchfab\.com/i.test(url) ? url.match(/[0-9a-f]{32}/i)?.[0] : undefined;
  return id && `https://sketchfab.com/models/${id}/embed?ui_infos=0&ui_hint=0&ui_watermark_link=0&ui_watermark=0&ui_inspector=0&ui_settings=0&ui_help=0&ui_vr=0&ui_stop=0&ui_fullscreen=0&ui_theme=dark&autostart=1&preload=1`;
}

/**
 * Get the URL of a virtual experience. digitaltmuseum.no only shows virtual experiences from Norwegian owners
 * and digitaltmuseum.se only from Swedish owners (owner codes "S-*"); the others give an error page.
 * digitaltmuseum.org shows all. The country domain is used when it matches both owner and language.
 * @param {string} uuid DM artifact UUID
 * @param {string} language Current language
 * @param {string} ownerCode Owner code. If unknown, .org is used
 * @returns {string|undefined} URL
 */
export const getDMVirtualExperienceUrl = (uuid?: string, language?: string, ownerCode?: string): string | undefined => {
  if (!uuid) { return undefined; }
  const isSwedishOwner = /^S-/i.test(ownerCode || "");
  const country = !ownerCode ? undefined
    : isSwedishOwner ? (language === "sv" ? "se" : undefined)
    : (["no", "nb", "nn", "se", "sma", "sms"].includes(language || "") ? "no" : undefined);
  return `https://digitaltmuseum.${country || "org"}/virtual-experiences/${uuid}`;
}

// ---------------------------------------------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------------------------------------------

/** Quote and escape a value for a Solr filter query. Exported for the other DM services */
export const solrQuote = (value: string | number): string => `"${String(value).trim().replace(/(["\\])/g, "\\$1")}"`;

/** Split a comma separated CMS value into trimmed, non-empty parts */
const splitList = (value: any): string[] => String(value || "").split(",").map(v => v.trim()).filter(Boolean);

/**
 * Run a query against DM's Solr API. Exported for the other DM services
 * @param {object} params Query parameters. Array values are added as repeated parameters
 * @returns {object} Solr response
 */
export const solrSelect = async (params: Record<string, string | number | (string | number)[] | undefined>) => {
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== "")
    .flatMap(([key, value]) => [value].flat().map(v => `${key}=${encodeURIComponent(String(v))}`))
    .join("&");

  const data = await fetchDMContent(`${DM_SEARCH_URL}select?wt=json&api.key=${DM_API_KEY}&${query}`);
  return data?.error ? Promise.reject({ message: "error.fetchError", error: data.error.msg }) : data;
}

/** Fetch a single object (simple_json mapping) */
export const fetchDMArtifact = (uniqueId: string) => fetchDMContent(`${DM_OBJECT_URL.replace("{id}", uniqueId)}&api.key=${DM_API_KEY}`);

/**
 * Get the experiences in a virtual experience. Only available in DM's native artifact API, not in simple_json
 * @param {string} uuid DM artifact UUID
 * @returns {DMExperience[]} Experiences
 */
export const fetchDMExperiences = async (uuid: string): Promise<DMExperience[]> => {
  const data = await fetchDMContent(`https://api.dimu.org/artifact/uuid/${uuid}`);
  return (data?.exhibitionMetadata?.exhibitionContents || [])
    .filter((content: any) => content?.contentId)
    .map((content: any) => ({
      contentId: content.contentId,
      type: content.exhibitionType?.type,
      name: content.name || content.data?.name,
      ingress: content.ingress || content.data?.ingress,
      previewDmsId: content.previewDmsId
    }));
}

/**
 * Find the virtual experience a part (e.g. `vm-scrollytell`) belongs to, and the part's content in it, so the part
 * can be opened inside the experience like on DM. The content is matched by type and name
 * @param {string} uuid DM UUID of the part
 * @returns {object} The experience's UUID and owner, and the content ID of the part (undefined if it was not found)
 */
export const resolveDMExperiencePart = async (uuid: string): Promise<{ uuid: string, ownerCode?: string, contentId?: string }> => {
  const part = await fetchDMContent(`https://api.dimu.org/artifact/uuid/${uuid}`);
  const parentUuid: string | undefined = part?.partOfExhibitionUuids?.[0];
  if (!parentUuid) { return Promise.reject({ message: "dm.contextNotFound" }); }
  const parent = await fetchDMContent(`https://api.dimu.org/artifact/uuid/${parentUuid}`);
  const type = String(part?.subtype || "").replace(/^vm-/, "");
  const name = (value: any) => String(value || "").trim().toLowerCase();
  const contents: any[] = (parent?.exhibitionMetadata?.exhibitionContents || []).filter((content: any) => content?.contentId);
  const ofType = contents.filter(content => content.exhibitionType?.type === type);
  const match = ofType.find(content => name(content.name || content.data?.name) === name(part?.title)) || (ofType.length === 1 ? ofType[0] : undefined);
  return { uuid: parentUuid, ownerCode: parent?.identifier?.owner, contentId: match?.contentId };
}

// ---------------------------------------------------------------------------------------------------------------
// Context: exhibition, folder or single object
// ---------------------------------------------------------------------------------------------------------------

/**
 * Look up a DM object by its unique ID. Exhibitions and folders get a filter for their objects.
 * Rejects if the object is missing, or is not an exhibition/folder and single objects are not allowed,
 * so a search is never widened by accident.
 * @param {string} uniqueId DM unique ID
 * @param {object} options allowSingleObject: also accept other objects (shown on their own)
 * @returns {DMContext} Context
 */
export const resolveDMContextByUniqueId = async (uniqueId: string, options: { allowSingleObject?: boolean } = {}): Promise<DMContext> => {
  const data = await solrSelect({ q: "*", fq: `artifact.uniqueId:${solrQuote(uniqueId)}`, rows: 1 });
  const doc = data?.response?.docs?.[0];
  const item = toDMItem(doc);
  const field = item.type && CONTEXT_FIELDS[item.type];

  if (!item.uuid || (!field && !options.allowSingleObject)) {
    console.error(`DM context ${uniqueId} is not an exhibition or folder (type: ${item.type})`);
    return Promise.reject({ message: "dm.contextNotFound" });
  }
  return { ...item, uniqueId, fq: field ? `${field}:${solrQuote(item.uuid)}` : undefined };
}

/**
 * Look up the DM object in a Kulturio media object of type "dm" (`dmcontent.object` or a collection)
 * @param {number|string} mediaId Kulturio media ID
 * @param {object} options See resolveDMContextByUniqueId
 * @returns {DMContext} Context
 */
export const resolveDMContext = async (mediaId: number | string, options: { allowSingleObject?: boolean } = {}): Promise<DMContext> => {
  const media = await fetchMedia(mediaId);
  const uniqueId = media?.identifier || media?.additional_metadata?.digitaltMuseum_artifactId;
  return uniqueId ? resolveDMContextByUniqueId(uniqueId, options) : Promise.reject({ message: "dm.contextNotFound" });
}

/**
 * Get the DM unique ID of a Kulturio media object of type "dm"
 * @param {number|string} mediaId Kulturio media ID
 * @returns {string|undefined} DM unique ID
 */
const getDMUniqueIdFromMedia = async (mediaId: number | string): Promise<string | undefined> => {
  const media = await fetchMedia(mediaId).catch(() => null);
  return media?.identifier || media?.additional_metadata?.digitaltMuseum_artifactId || undefined;
}

/**
 * Resolve the collections of a dynamic search (`dmcontent.collections`: exhibitions and folders) to one
 * context that matches objects in any of them. Rejects if one can not be resolved, so the search is never widened
 * @param {number[]|string[]} mediaIds Kulturio media IDs
 * @returns {DMContext} Context
 */
export const resolveDMCollections = async (mediaIds: (number | string)[]): Promise<DMContext> => {
  const contexts = await Promise.all(mediaIds.map(mediaId => resolveDMContext(mediaId)));
  return contexts.length === 1 ? contexts[0] : { ...contexts[0], fq: `(${contexts.map(context => context.fq).join(" OR ")})` };
}

/**
 * Resolve a hand-picked selection (`dmcontent.objects`) to a context with its objects in the CMS order.
 * Objects that can not be found are left out; rejects if none are found
 * @param {number[]|string[]} mediaIds Kulturio media IDs
 * @returns {DMContext} Context
 */
export const resolveDMSelection = async (mediaIds: (number | string)[]): Promise<DMContext> => {
  const uniqueIds = [...new Set((await Promise.all(mediaIds.slice(0, DM_MANUAL_MAX).map(getDMUniqueIdFromMedia))).filter(Boolean) as string[])];
  if (uniqueIds.length === 0) { return Promise.reject({ message: "dm.contextNotFound" }); }
  return { uniqueId: "", fq: `artifact.uniqueId:(${uniqueIds.map(solrQuote).join(" OR ")})`, order: uniqueIds };
}

/**
 * Sort Solr results in the order of a hand-picked selection (the CMS order). Results not in the order go last
 * @param {object[]} docs Solr documents
 * @param {string[]} order Unique IDs in the CMS order
 * @returns {object[]} Sorted copy
 */
export const sortDMDocsByOrder = (docs: any[], order: string[]): any[] => {
  const position = (doc: any) => {
    const index = order.indexOf(doc?.["artifact.uniqueId"]);
    return index === -1 ? order.length : index;
  };
  return [...docs].sort((a, b) => position(a) - position(b));
}

// ---------------------------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------------------------

/**
 * Build the Solr query for object types from the CMS. Real DM types (Thing, Photograph, ...) and the
 * pseudo types (School, VirtualExperience, Article) can be mixed
 * @param {string[]} types Object types
 * @returns {string|undefined} Solr query, or undefined if there are no types
 */
export const getDMObjectTypeQuery = (types: string[]): string | undefined => {
  const keys = [...new Set(types.map(t => String(t || "").trim()).filter(Boolean).map(t => TYPE_ALIASES[t] || t))];
  const plain = keys.filter(key => !TYPE_QUERIES[key]);
  const parts = [
    ...(plain.length > 0 ? [`artifact.type:(${plain.map(solrQuote).join(" OR ")})`] : []),
    ...keys.filter(key => TYPE_QUERIES[key]).map(key => TYPE_QUERIES[key])
  ];
  return parts.length > 1 ? `(${parts.join(" OR ")})` : parts[0];
}

/**
 * Build the base search from the CMS
 * @param {object} dmcontent Search settings (`content.dmcontent.search`)
 * @param {DMContext} context Exhibition, folder or hand-picked selection. Optional
 * @param {string} sortSetting Default sort (`content.settings.sort`). Optional
 * @returns {DMBaseSearch} Base search
 */
export const buildDMBaseSearch = (dmcontent: any, context?: DMContext | null, sortSetting?: string): DMBaseSearch => {
  const owners = splitList(dmcontent?.ownerCode);
  // An array in new CMS versions, a single string in older ones
  const objectTypes = [dmcontent?.objectType].flat().filter(type => typeof type === "string" && type.length > 1);
  const objectTypeQuery = getDMObjectTypeQuery(objectTypes);
  const license = dmcontent?.license;

  const fq = [
    context?.fq,
    owners.length > 0 ? `identifier.owner:(${owners.map(solrQuote).join(" OR ")})` : undefined,
    objectTypeQuery,
    // All tags must match. Subjects are case sensitive in DM, so "gitar" also matches "Gitar"
    ...splitList(dmcontent?.tags).map(tag => {
      const variants = [...new Set([tag, tag.charAt(0).toUpperCase() + tag.slice(1), tag.toLowerCase()])];
      return `artifact.ingress.subjects:(${variants.map(solrQuote).join(" OR ")})`;
    }),
    // Objects without images are shown with a placeholder otherwise
    dmcontent?.onlyWithImages === true ? "artifact.hasPictures:true" : undefined,
    // "*" means all licenses
    license && license !== "*" ? `artifact.license:${solrQuote(license)}` : undefined
  ].filter((value): value is string => Boolean(value));

  // A known sort key, or a DM field name (sorted descending). Hand-picked selections keep the CMS order
  const isFieldName = typeof sortSetting === "string" && !SORTS[sortSetting] && /^[a-zA-Z.]+$/.test(sortSetting);
  const sortKey = context?.order ? "manual"
    : sortSetting && SORTS[sortSetting] && sortSetting !== "manual" ? sortSetting
    : isFieldName ? "cms" : DM_DEFAULT_SORT_KEY;

  return {
    q: dmcontent?.query?.trim?.() || "*",
    fq,
    sortKey,
    sort: isFieldName ? `artifact.${sortSetting} desc` : undefined,
    order: context?.order
  };
}

/**
 * Build the base search for an exhibition or folder opened from a DM view. The CMS query, owner, types,
 * tags and license describe the parent list, so only `onlyWithImages` and the sort are kept
 * @param {object} search Search settings of the parent view (`dmcontent.search`)
 */
export const buildDMSubContextSearch = (search: any, context: DMContext, sortSetting?: string): DMBaseSearch => {
  return buildDMBaseSearch({ onlyWithImages: search?.onlyWithImages }, context, sortSetting);
}

/**
 * Get the Solr sort for a sort key
 * @param {string} key SORTS key, or "cms" for the DM field name in the base search
 * @param {DMBaseSearch} base Base search
 * @param {number} seed Seed for random sorting
 * @returns {string|undefined} Solr sort
 */
export const getDMSortQuery = (key: string, base: DMBaseSearch, seed: number): string | undefined => {
  if (key === "cms") { return base.sort; }
  const sort = SORTS[key];
  return typeof sort === "function" ? sort(seed) : sort;
}

/**
 * Convert filters chosen on screen to tagged filter queries. The tags make it possible to ignore a filter
 * when counting the values of that same filter (multi-select faceting)
 */
const getUserFilterQueries = (filters: DMUserFilters): string[] => {
  const period = DM_PERIODS.find(p => p.key === filters?.period);
  return [
    filters?.type && `{!tag=type}${TYPE_QUERIES[filters.type] || `artifact.type:${solrQuote(filters.type)}`}`,
    period && `{!tag=period}${period.query}`,
    filters?.owner && `{!tag=owner}identifier.owner:${solrQuote(filters.owner)}`,
    filters?.subject && `{!tag=subject}artifact.ingress.subjects:${solrQuote(filters.subject)}`
  ].filter((value): value is string => Boolean(value));
}

/**
 * Search DM. The base search is always applied, filters chosen on screen narrow it down
 * @param {DMBaseSearch} base Base search
 * @param {DMUserFilters} filters Filters chosen on screen
 * @param {object} options Paging and sorting
 * @returns {object} Solr response
 */
export const searchDM = (base: DMBaseSearch, filters: DMUserFilters, options: { start?: number, sort?: string, rows?: number } = {}) => {
  return solrSelect({
    q: base.q,
    fq: [...base.fq, ...getUserFilterQueries(filters)],
    sort: options.sort || base.sort,
    start: options.start || 0,
    rows: options.rows || PAGE_SIZE
  });
}

/**
 * Get the available filter values within the base search and the other filters chosen on screen
 * @param {DMBaseSearch} base Base search
 * @param {DMUserFilters} filters Filters chosen on screen
 * @returns {DMFacets} Values and counts per filter
 */
export const fetchDMFacets = async (base: DMBaseSearch, filters: DMUserFilters): Promise<DMFacets> => {
  const data = await solrSelect({
    q: base.q,
    fq: [...base.fq, ...getUserFilterQueries(filters)],
    rows: 0,
    facet: "true",
    "facet.mincount": 1,
    "facet.limit": 40,
    "facet.field": ["{!ex=type}artifact.type", "{!ex=owner}identifier.owner", "{!ex=subject}artifact.ingress.subjects"],
    "facet.query": [
      ...DM_PERIODS.map(p => `{!ex=period key=${p.key}}${p.query}`),
      ...Object.entries(TYPE_QUERIES).map(([key, query]) => `{!ex=type key=type_${key}}${query}`)
    ]
  });

  const fields = data?.facet_counts?.facet_fields || {};
  const queries: Record<string, number> = data?.facet_counts?.facet_queries || {};

  // Solr returns facet fields as a flat list: [value, count, value, count, ...]
  const pairs = (list: any[] = []): DMFacet[] => list.flatMap((value, i) => i % 2 === 0 ? [{ value, count: list[i + 1] }] : []);
  const counted = (entries: [string, number][]): DMFacet[] => entries.filter(([, count]) => count > 0).map(([value, count]) => ({ value, count }));

  // "Article" is replaced by the pseudo types (School, VirtualExperience and other articles)
  const types = [
    ...pairs(fields["artifact.type"]).filter(type => type.value !== "Article"),
    ...counted(Object.keys(TYPE_QUERIES).map(key => [key, queries[`type_${key}`] || 0]))
  ].sort((a, b) => b.count - a.count);

  return {
    types,
    owners: pairs(fields["identifier.owner"]),
    subjects: pairs(fields["artifact.ingress.subjects"]),
    periods: counted(DM_PERIODS.map(p => [p.key, queries[p.key] || 0]))
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Owner names
// ---------------------------------------------------------------------------------------------------------------

let museumNames: Promise<Record<string, string>> | null = null;

/** Load DM's museum register once: owner code (ekultur_id) → official museum name */
const loadMuseumNames = (): Promise<Record<string, string>> => {
  museumNames = museumNames || fetchDMContent(MUSEUM_API_URL).then(data => Object.fromEntries(
    (data?.museums || []).filter((museum: any) => museum?.ekultur_id && museum?.name).map((museum: any) => [museum.ekultur_id, museum.name])
  )).catch(() => {
    museumNames = null;
    return {};
  });
  return museumNames;
}

/** Owner code → owner name, shared between views */
const ownerNameCache: Record<string, Promise<string | undefined>> = {};

/**
 * Get the name of an owner (museum). Uses DM's museum register, and falls back to the owner name
 * in one of the owner's objects
 * @param {string} ownerCode Owner code
 * @param {string} sampleUniqueId Unique ID of an object owned by this owner. Optional
 * @returns {string|undefined} Owner name
 */
export const getDMOwnerName = (ownerCode: string, sampleUniqueId?: string): Promise<string | undefined> => {
  ownerNameCache[ownerCode] = ownerNameCache[ownerCode] || (async () => {
    const names = await loadMuseumNames();
    if (names[ownerCode]) { return names[ownerCode]; }

    const uniqueId = sampleUniqueId || (await solrSelect({ q: "*", fq: `identifier.owner:${solrQuote(ownerCode)}`, rows: 1 }).catch(() => null))?.response?.docs?.[0]?.["artifact.uniqueId"];
    const artifact = uniqueId ? await fetchDMArtifact(uniqueId).catch(() => null) : null;
    return artifact?.partOfCollection?.owner?.name || undefined;
  })().catch(() => {
    delete ownerNameCache[ownerCode];
    return undefined;
  });
  return ownerNameCache[ownerCode];
}
